import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { buttonClass, Spinner } from '../../components/ui.tsx';
import { api, ApiError } from '../../lib/api.ts';
import { meQueryKey } from '../../lib/auth.ts';
import s from './auth.module.css';

/**
 * Sähköpostin vaihdon vahvistus: /vahvista-sahkoposti#<tunniste>. Toimii kirjautumatta,
 * koska linkki itse todistaa uuden osoitteen omistuksen.
 */
export function ConfirmEmailPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const started = useRef(false);
  const [noToken, setNoToken] = useState(false);

  const confirm = useMutation({
    mutationFn: (token: string) =>
      api<{ email: string }>('/auth/email-change/confirm', { method: 'POST', body: { token } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: meQueryKey }),
  });

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = window.location.hash.slice(1);
    window.history.replaceState(null, '', window.location.pathname);
    if (token) confirm.mutate(token);
    else setNoToken(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const taken = confirm.error instanceof ApiError && confirm.error.code === 'email_taken';

  return (
    <div className={s.center}>
      <div className={s.centerCard}>
        {confirm.isSuccess ? (
          <>
            <h1 className={s.title}>{t('emailChange.doneTitle')}</h1>
            <p className={s.lead}>{t('emailChange.doneText', { email: confirm.data.email })}</p>
            <Link to="/" className={buttonClass('primary')}>
              {t('emailChange.continue')}
            </Link>
          </>
        ) : confirm.isError || noToken ? (
          <>
            <h1 className={s.title}>{t('emailChange.failedTitle')}</h1>
            <p className={s.lead}>{taken ? t('emailChange.taken') : t('emailChange.failedText')}</p>
            <Link to="/" className={buttonClass('primary')}>
              {t('emailChange.continue')}
            </Link>
          </>
        ) : (
          <>
            <Spinner />
            <p className={s.lead}>{t('verify.checking')}</p>
          </>
        )}
      </div>
    </div>
  );
}
